import { useCallback, useEffect, useRef, useState } from "react";

interface UseAutoFontSizeProps {
  text: string;
  maxFontSize?: number;
  minFontSize?: number;
}

export function useAutoFontSize({
    text,
    maxFontSize = 16,
    minFontSize = 8,
}: UseAutoFontSizeProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLParagraphElement>(null);

    const [fontSize, setFontSize] = useState(maxFontSize);

    const resize = useCallback(() => {
        const container = containerRef.current;
        const textElement = textRef.current;

        if (!container || !textElement) return;

        let size = maxFontSize;

        const fits = () =>
            textElement.scrollWidth <= container.clientWidth && textElement.scrollHeight <= container.clientHeight;

        textElement.style.fontSize = `${size}px`;

        while (!fits() && size > minFontSize) {
            size--;
            textElement.style.fontSize = `${size}px`;
        }

        setFontSize(size);
    }, [maxFontSize, minFontSize]);

    useEffect(() => {
        resize();
    }, [text, resize]);

    useEffect(() => {
        const handleResize = () => {
            requestAnimationFrame(resize);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [resize]);

    return { containerRef, textRef, fontSize };
}